import React, { useEffect, useState } from 'react';
import { updateConfig } from '../../api/api';
import { getConfig } from '../../api/api';
import featureFlagsConfig from '../../constants/featureFlagsConfig.json';
import defaultProjectConfig from '../../constants/defaultProjectConfig.json';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    Typography,
    Grid,
    Paper,
    Card,
    CardContent,
    CardHeader,
    Divider,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { Createclient, updateClient, getClients } from '../../api';
import { useDispatch } from 'react-redux';
import { hideLoader, showLoader } from '../../store/loaderSlice';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export interface Client {
    project_id?: string;
    index_name: string;
    storage_class: string;
    email_domain: string;
    title: string;
    config: Record<string, any>;
    feature_flags: Record<string, any>;
    archetype: string;
}

// Extend Client type locally to include CWStatus for type safety
export interface CWStatus {
  red?: string;
  grey?: string;
  green?: string;
}
export interface ProjectWithCWStatus extends Client {
  CWStatus?: CWStatus;
}

const ProjectDetail: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();

    const isNew = id === 'new';
    const [project, setProject] = useState<ProjectWithCWStatus>({
        index_name: '',
        storage_class: 'AzureCosmosPostgreSQLConnector',
        email_domain: isNew ? '' : '',
        title: isNew ? defaultProjectConfig.title : '',
        config: isNew ? { login: defaultProjectConfig.ui_config.login } : {},
        feature_flags: isNew ? defaultProjectConfig.feature_flags : {},
        archetype: isNew ? defaultProjectConfig.archetype : '',
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [globalConfig, setGlobalConfig] = useState<any>({});
    const [subHeadingText, setSubHeadingText] = useState<string>('');

    // Utility to deeply ensure palette/typography structure exists
    const normalizeConfig = (config: any = {}) => {
        // Handle both nested and flat config structures
        const safe: any = { ...config };
        
        // If we have a nested config structure, flatten it
        if (safe.config && typeof safe.config === 'object') {
            // Extract nested palette and typography
            if (safe.config.palette) safe.palette = safe.config.palette;
            if (safe.config.typography) safe.typography = safe.config.typography;
            // Remove the nested config to avoid duplication
            delete safe.config;
        }
        
        // Ensure all required properties exist
        safe.palette = safe.palette || {};
        safe.palette.primary = safe.palette.primary || {};
        safe.palette.secondary = safe.palette.secondary || {};
        safe.palette.error = safe.palette.error || {};
        safe.palette.text = safe.palette.text || {};
        safe.typography = safe.typography || {};
        return safe;
    };

    useEffect(() => {
        const fetchGlobalConfig = async () => {
            try {
                if (isNew) {
                    // For new projects, use the default config from our JSON file
                    console.log('Using default project configuration');
                    
                    // For new projects, use the default config from our JSON file
                    // Extract config directly from defaultProjectConfig
                    setGlobalConfig(normalizeConfig(defaultProjectConfig.config));
                    setSubHeadingText((defaultProjectConfig as any).subHeadingText || '');
                    
                    // Note: index_name is intentionally left blank as per requirements
                } else {
                    // For existing projects, fetch the config from the API
                    const configResponse = await getConfig(String(id));
                    
                    // Access the raw response data through the browser console to inspect structure
                    console.log('Config response:', configResponse);
                    
                    // Set the global config
                    if (configResponse?.uiConfig) {
                        setGlobalConfig(normalizeConfig(configResponse.uiConfig));
                        setSubHeadingText(configResponse.uiConfig.subHeadingText || '');
                    }
                    
                    // Make a direct API call to get the index_name
                    const response = await fetch(`/api/client-mgmt/config/${id}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('user') || '{}').access_token}`
                        }
                    });
                    const data = await response.json();
                    
                    // Update the index_name from the API response
                    if (data?.data?.index_name) {
                        console.log('Setting index_name from API:', data.data.index_name);
                        setProject(prev => ({
                            ...prev,
                            index_name: data.data.index_name
                        }));
                    } else {
                        console.warn('index_name not found in API response:', data);
                        // If we don't get index_name from the config endpoint, try to get it from the client data
                        if (id) {
                            // Use the loadClient function logic to find the client by ID
                            const clientResponse = await getClients('', 1, 100);
                            if (clientResponse?.success && clientResponse?.data) {
                                const client = clientResponse.data.find((c: any) => c.client_id === Number(id));
                                console.log('Retrieved client data for index_name:', client);
                                if (client?.client_name) {
                                    setProject(prev => ({
                                        ...prev,
                                        index_name: client.client_name
                                    }));
                                }
                            }
                        }
                    }
                }
            } catch (e) {
                console.error('Failed to fetch global config', e);
            }
        };
        fetchGlobalConfig();
    }, [isNew, id]);

    useEffect(() => {
        if (!isNew && id) {
            loadClient(id);
        }
    }, [id]);

    // Robust project loader: fetch all pages until project is found or no more pages
    const loadClient = async (projectId: string) => {
        try {
            setLoading(true);
            dispatch(showLoader());

            let foundClient = null;
            let page = 1;
            const pageSize = 100;
            let keepFetching = true;

            while (keepFetching && !foundClient) {
                const response = await getClients('', page, pageSize);
                if (response && response.success) {
                    const projectData = response.data.find((c: any) => String(c.client_id) === String(projectId));
                    if (projectData) {
                        foundClient = { ...projectData, project_id: projectData.client_id };
                        break;
                    }
                    // If less than pageSize, we're at the last page
                    if (!response.data || response.data.length < pageSize) {
                        keepFetching = false;
                    } else {
                        page += 1;
                    }
                } else {
                    const errorMsg = response?.payload || 'Failed to fetch project details';
                    toast.error(errorMsg);
                    navigate('/homepage/project');
                    return;
                }
            }

            if (foundClient) {
                // Map client_id to project_id and ensure client_name is mapped to index_name
                const mappedClient = {
                    ...foundClient,
                    project_id: foundClient.client_id,
                    index_name: foundClient.client_name || 'default', // Map client_name to index_name with fallback
                    config: normalizeConfig(foundClient.config)
                };
                
                // Log the original client data to debug
                console.log('Original client data:', foundClient);
                
                console.log('Mapped client data:', mappedClient);
                setProject(prev => ({
                  ...prev,
                  ...mappedClient
                }));
            } else {
                console.warn(`Client with id ${projectId} not found after fetching all pages.`);
                toast.error('Project not found');
                navigate('/homepage/project');
            }
        } catch (error) {
            console.error('Error loading project:', error);
            toast.error('An error occurred while loading project details');
            navigate('/homepage/project');
        } finally {
            setLoading(false);
            dispatch(hideLoader());
        }
    };


    const handleTextFieldChange = (field: keyof Client) => (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const value = event.target.value;
        setProject((prev: ProjectWithCWStatus) => ({
            ...prev,
            [field]: value,
        }));
        
        // Clear error when field is edited
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!project.index_name.trim()) {
            newErrors.index_name = 'Index name is required';
        } else if (isNew && !/^[a-z]+$/.test(project.index_name)) {
            newErrors.index_name = 'Index name must contain only lowercase letters (a-z)';
        }
        if (!project.storage_class.trim()) {
            newErrors.storage_class = 'Storage class is required';
        }
        if (!project.email_domain.trim()) {
            newErrors.email_domain = 'Email domain is required';
        }
        if (!project.title.trim()) {
            newErrors.title = 'Title is required';
        }
        if (!project.archetype.trim()) {
            newErrors.archetype = 'Archetype is required';
        }
        // config and feature_flags can be empty objects by default
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
    console.log('handleSave called');
        const valid = validateForm();
    console.log('Validation result:', valid);
    if (!valid) {
            toast.error('Please correct the errors in the form');
            return;
        }
        try {
            dispatch(showLoader());
            // Prepare payload to match backend schema
            let projectPayload: any = { ...project };
            if (!isNew && project.project_id) {
                projectPayload.project_id = Number(project.project_id);
            }
            // Map index_name to project_name for API compatibility
            projectPayload.project_name = projectPayload.index_name;
            delete projectPayload.index_name; // Remove index_name as it's not expected by the API
            
            // Ensure all fields required by ClientUpdateSchema are present
            const requiredFields = [
                'project_id', 'project_name', 'storage_class', 'email_domain', 'title', 'config', 'feature_flags', 'archetype'
            ];
            requiredFields.forEach(field => {
                if (!(field in projectPayload)) {
                    projectPayload[field] = field === 'config' || field === 'feature_flags' ? {} : '';
                }
            });
            const payload = [projectPayload]; // Always an array
            console.log('About to call API with payload:', payload);
            
            // Different flow for new vs existing projects
            if (isNew) {
                console.log('Creating new project');
                // Step 1: Create the client/project first with all required fields according to ClientCreateSchema
                const clientPayload = [{
                    client_name: projectPayload.project_name,
                    storage_class: projectPayload.storage_class || '',
                    email_domain: projectPayload.email_domain || '',
                    title: projectPayload.title || '',
                    config: projectPayload.config || {},
                    feature_flags: projectPayload.feature_flags || {},
                    archetype: projectPayload.archetype || ''
                }];
                console.log('Client creation payload:', clientPayload);
                const createResponse = await Createclient(clientPayload);
                console.log('Create project response:', createResponse);
                
                if (!createResponse?.success) {
                    const error = createResponse?.payload?.['0'] || 'Something went wrong';
                    toast.error(`Project creation failed: ${error}`);
                    dispatch(hideLoader());
                    return;
                }
                
                // Step 2: Extract the client_id from the response
                const createdProject = createResponse.data[0];
                const newClientId = createdProject.client_id;
                console.log('New client ID:', newClientId);
                
                if (!newClientId) {
                    toast.error('Failed to get project ID from server response');
                    dispatch(hideLoader());
                    return;
                }
                
                // Step 3: Update the config with the new client_id
                try {
                    // Prepare complete config payload including project-specific config
                    const configPayload = {
                        // Include UI config from globalConfig
                        login: globalConfig.login,
                        subHeadingText: subHeadingText,
                        // Flatten the palette and typography directly into the config
                        palette: globalConfig.palette,
                        typography: globalConfig.typography,
                        CWStatus: globalConfig.CWStatus,
                        // Include project-specific config
                        ...projectPayload,
                        // Set the correct client_id/project_id
                        project_id: newClientId
                    };
                    console.log(`Calling updateConfig for new project with ID ${newClientId}:`, configPayload);
                    await updateConfig(String(newClientId), configPayload);
                    toast.success('Project created and config updated successfully');
                    navigate('/homepage/project');
                } catch (err) {
                    console.error('Config update failed for new project:', err);
                    toast.error('Project was created but config update failed');
                    navigate('/homepage/project');
                }
            } else {
                // For existing projects, use the original flow
                console.log('Updating existing project');
                const response = await updateClient(payload);
                console.log('Update project response:', response);
                
                // Always try to update config after project update
                let configSuccess = false;
                try {
                    // Prepare complete config payload including project-specific config
                    const configPayload = {
                        // Include UI config from globalConfig
                        login: globalConfig.login,
                        subHeadingText: subHeadingText,
                        // Flatten the palette and typography directly into the config
                        palette: globalConfig.palette,
                        typography: globalConfig.typography,
                        CWStatus: globalConfig.CWStatus,
                        // Include project-specific config
                        ...projectPayload,
                    };
                    console.log('Calling updateConfig with:', configPayload);
                    await updateConfig(String(project.project_id), configPayload);
                    configSuccess = true;
                    console.log('Config update success');
                } catch (err) {
                    configSuccess = false;
                    console.error('Config update failed', err);
                }
                
                if (response?.success && configSuccess) {
                    toast.success('Project and config updated successfully');
                    navigate('/homepage/project');
                } else if (!response?.success && !configSuccess) {
                    const error = response?.payload?.['0'] || 'Something went wrong';
                    toast.error(`Project and config update failed: ${error}`);
                } else if (!response?.success) {
                    const error = response?.payload?.['0'] || 'Something went wrong';
                    toast.error(`Project update failed: ${error}`);
                } else if (!configSuccess) {
                    toast.error('Config update failed');
                }
            }
        } catch (err) {
            console.error('Error in handleSave:', err);
            toast.error('An error occurred while saving the project');
        } finally {
            dispatch(hideLoader());
        }
    };


    const handleCancel = () => {
        navigate('/homepage/project');
    };

    return (
        <Box sx={{ width: '100%', p: 3, mt: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={handleCancel}
                    sx={{ mr: 2 }}
                >
                    Back to Projects
                </Button>
                <Typography variant="h5" component="h1">
                    {isNew ? 'Create New Project' : 'Edit Project'}
                </Typography>
            </Box>
            
            <Paper sx={{ p: 3, mb: 3 }}>
                <Card>
                    <CardHeader title="Project Information" />
                    <Divider />
                    <CardContent>
                        <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
        <TextField
            label="Index Name"
            value={project.index_name}
            onChange={handleTextFieldChange('index_name')}
            fullWidth
            required
            error={!!errors.index_name}
            helperText={errors.index_name || (isNew ? 'Only lowercase letters (a-z) allowed' : 'Index name cannot be changed after creation')}
            margin="normal"
            disabled={!isNew} // Disable the field when editing an existing project
            InputProps={{
                readOnly: !isNew, // Make it read-only when editing an existing project
                sx: { bgcolor: !isNew ? 'rgba(0, 0, 0, 0.05)' : 'inherit' } // Grey background when disabled
            }}
        />
    </Grid>
    <Grid item xs={12} md={6}>
        <TextField
            label="Storage Class"
            value={project.storage_class}
            fullWidth
            required
            margin="normal"
            InputProps={{ readOnly: true }}
            helperText={"This value is fixed and cannot be edited."}
            disabled
        />
    </Grid>
    <Grid item xs={12} md={6}>
        <TextField
            label="Email Domain"
            value={project.email_domain}
            onChange={handleTextFieldChange('email_domain')}
            fullWidth
            required
            error={!!errors.email_domain}
            helperText={errors.email_domain}
            margin="normal"
        />
    </Grid>
    <Grid item xs={12} md={6}>
        <TextField
            label="Title"
            value={project.title}
            onChange={handleTextFieldChange('title')}
            fullWidth
            required
            error={!!errors.title}
            helperText={errors.title}
            margin="normal"
        />
    </Grid>
    <Grid item xs={12} md={6}>
        <TextField
            label="Sub Heading Text"
            value={subHeadingText}
            onChange={(e) => setSubHeadingText(e.target.value)}
            fullWidth
            margin="normal"
            helperText='Shown on homepage below the main heading'
        />
    </Grid>
    <Grid item xs={12} md={6}>
        <Select
            label="Archetype"
            value={project.archetype}
            onChange={event => {
                setProject(prev => ({ ...prev, archetype: event.target.value }));
                if (errors.archetype) {
                    setErrors(prev => ({ ...prev, archetype: '' }));
                }
            }}
            fullWidth
            required
            displayEmpty
            error={!!errors.archetype}
            sx={{ mt: 2 }}
        >
            <MenuItem value="" disabled>
                <em>Select Archetype</em>
            </MenuItem>
            <MenuItem value="Tendering">Tendering</MenuItem>
            <MenuItem value="Negotiations">Negotiations</MenuItem>
            <MenuItem value="Contracting">Contracting</MenuItem>
            <MenuItem value="Monitoring">Monitoring</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
        </Select>
        {errors.archetype && (
            <Typography color="error" variant="caption">{errors.archetype}</Typography>
        )}
    </Grid>
    <Grid item xs={12} md={6}>
            <TextField
                label="Config (JSON)"
                value={JSON.stringify(project.config || {})}
                onChange={e => {
                    let value = e.target.value;
                    try {
                        const parsed = value ? JSON.parse(value) : {};
                        setProject(prev => ({ ...prev, config: normalizeConfig(parsed) }));
                        setErrors(prev => ({ ...prev, config: '' }));
                    } catch {
                        setErrors(prev => ({ ...prev, config: 'Invalid JSON' }));
                    }
                }}
                fullWidth
                multiline
                rows={3}
                error={!!errors.config}
                helperText={errors.config || 'Enter a JSON object'}
                margin="normal"
            />
    </Grid>
    {/* Features Wrapper Section */}
    <Grid item xs={12}>
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>Features</Typography>
        <Divider sx={{ mb: 3 }} />
        {/* Subsection: Features */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Feature Toggles</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {featureFlagsConfig.features.map((feature) => (
              <Grid item xs={12} sm={6} md={3} key={feature.key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!project.feature_flags?.features?.[feature.key]}
                      onChange={() => {
                        setProject((prev: ProjectWithCWStatus) => ({
                          ...prev,
                          feature_flags: {
                            ...prev.feature_flags,
                            features: {
                              ...((prev.feature_flags && prev.feature_flags.features) || {}),
                              [feature.key]: !prev.feature_flags?.features?.[feature.key]
                            },
                            pipeline: prev.feature_flags?.pipeline || {},
                            question: prev.feature_flags?.question || {},
                            scenarios: prev.feature_flags?.scenarios || {},
                            fileShared: prev.feature_flags?.fileShared ?? false
                          }
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label={feature.label}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        {/* Subsection: Pipeline - File Level */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Pipeline - File Level</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {featureFlagsConfig.pipeline.fileLevel.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!project.feature_flags?.pipeline?.fileLevel?.[item.key]}
                      onChange={() => {
                        setProject((prev: ProjectWithCWStatus) => ({
                          ...prev,
                          feature_flags: {
                            ...prev.feature_flags,
                            pipeline: {
                              ...((prev.feature_flags && prev.feature_flags.pipeline) || {}),
                              fileLevel: {
                                ...((prev.feature_flags?.pipeline && prev.feature_flags.pipeline.fileLevel) || {}),
                                [item.key]: !prev.feature_flags?.pipeline?.fileLevel?.[item.key]
                              },
                              contractLevel: prev.feature_flags?.pipeline?.contractLevel || {}
                            },
                            features: prev.feature_flags?.features || {},
                            question: prev.feature_flags?.question || {},
                            scenarios: prev.feature_flags?.scenarios || {},
                            fileShared: prev.feature_flags?.fileShared ?? false
                          }
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label={item.label}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        {/* Subsection: Pipeline - Contract Level */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Pipeline - Contract Level</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {featureFlagsConfig.pipeline.contractLevel.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!project.feature_flags?.pipeline?.contractLevel?.[item.key]}
                      onChange={() => {
                        setProject((prev: ProjectWithCWStatus) => ({
                          ...prev,
                          feature_flags: {
                            ...prev.feature_flags,
                            pipeline: {
                              ...((prev.feature_flags && prev.feature_flags.pipeline) || {}),
                              contractLevel: {
                                ...((prev.feature_flags?.pipeline && prev.feature_flags.pipeline.contractLevel) || {}),
                                [item.key]: !prev.feature_flags?.pipeline?.contractLevel?.[item.key]
                              },
                              fileLevel: prev.feature_flags?.pipeline?.fileLevel || {}
                            },
                            features: prev.feature_flags?.features || {},
                            question: prev.feature_flags?.question || {},
                            scenarios: prev.feature_flags?.scenarios || {},
                            fileShared: prev.feature_flags?.fileShared ?? false
                          }
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label={item.label}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        {/* Subsection: Scenarios */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Scenarios Settings</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {featureFlagsConfig.scenarios.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!project.feature_flags?.scenarios?.[item.key]}
                      onChange={() => {
                        setProject((prev: ProjectWithCWStatus) => ({
                          ...prev,
                          feature_flags: {
                            ...prev.feature_flags,
                            scenarios: {
                              ...((prev.feature_flags && prev.feature_flags.scenarios) || {}),
                              [item.key]: !prev.feature_flags?.scenarios?.[item.key]
                            },
                            pipeline: prev.feature_flags?.pipeline || {},
                            features: prev.feature_flags?.features || {},
                            question: prev.feature_flags?.question || {},
                            fileShared: prev.feature_flags?.fileShared ?? false
                          }
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label={item.label}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        {/* Subsection: RTM Export Prompts */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>RTM Export Prompts</Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Override the default prompts used when generating RTM export summaries. Leave blank to use the built-in defaults. Available template variables: {'{detailed_analysis}'}, {'{scenario_detail}'}, {'{reference_context}'}, {'{summary_extra_detail}'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Summarize Prompt"
                value={project.config?.rtm_export?.summarize_prompt ?? ''}
                onChange={e => setProject(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    rtm_export: {
                      ...((prev.config?.rtm_export) || {}),
                      summarize_prompt: e.target.value,
                    },
                  },
                }))}
                fullWidth
                multiline
                rows={6}
                margin="dense"
                helperText="Custom prompt for summarizing findings. Leave blank for default."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Extract References Prompt"
                value={project.config?.rtm_export?.extract_references_prompt ?? ''}
                onChange={e => setProject(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    rtm_export: {
                      ...((prev.config?.rtm_export) || {}),
                      extract_references_prompt: e.target.value,
                    },
                  },
                }))}
                fullWidth
                multiline
                rows={6}
                margin="dense"
                helperText="Custom prompt for extracting page/clause references. Leave blank for default."
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Grid>
    {/* Styling Wrapper Section */}
    <Grid item xs={12}>
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>Styling</Typography>
        <Divider sx={{ mb: 3 }} />
        {/* Palette Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Palette</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {/* Primary Colors */}
            <Grid item xs={12} md={6} lg={4}>
              <Typography sx={{ mb: 1 }}>Primary</Typography>
              <TextField label="Main"
  value={globalConfig.palette?.primary?.main ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      primary: {
        ...((prev.palette && prev.palette.primary) || {}),
        main: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
              <TextField label="Light"
  value={globalConfig.palette?.primary?.light ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      primary: {
        ...((prev.palette && prev.palette.primary) || {}),
        light: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
              <TextField label="Contrast Text"
  value={globalConfig.palette?.primary?.contrastText ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      primary: {
        ...((prev.palette && prev.palette.primary) || {}),
        contrastText: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
            </Grid>
            {/* Secondary Colors */}
            <Grid item xs={12} md={6} lg={4}>
              <Typography sx={{ mb: 1 }}>Secondary</Typography>
              <TextField label="Main"
  value={globalConfig.palette?.secondary?.main ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      secondary: {
        ...((prev.palette && prev.palette.secondary) || {}),
        main: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
              <TextField label="Light"
  value={globalConfig.palette?.secondary?.light ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      secondary: {
        ...((prev.palette && prev.palette.secondary) || {}),
        light: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
              <TextField label="Dark"
  value={globalConfig.palette?.secondary?.dark ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      secondary: {
        ...((prev.palette && prev.palette.secondary) || {}),
        dark: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
              <TextField label="Contrast Text"
  value={globalConfig.palette?.secondary?.contrastText ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      secondary: {
        ...((prev.palette && prev.palette.secondary) || {}),
        contrastText: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
            </Grid>
            {/* Error Colors */}
            <Grid item xs={12} md={6} lg={4}>
              <Typography sx={{ mb: 1 }}>Error</Typography>
              <TextField label="Main"
  value={globalConfig.palette?.error?.main ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      error: {
        ...((prev.palette && prev.palette.error) || {}),
        main: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
            </Grid>
            {/* Text Colors */}
            <Grid item xs={12} md={6} lg={4}>
              <Typography sx={{ mb: 1 }}>Text</Typography>
              <TextField label="Primary"
  value={globalConfig.palette?.text?.primary ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      text: {
        ...((prev.palette && prev.palette.text) || {}),
        primary: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
              <TextField label="Secondary"
  value={globalConfig.palette?.text?.secondary ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    palette: {
      ...prev.palette,
      text: {
        ...((prev.palette && prev.palette.text) || {}),
        secondary: e.target.value
      }
    }
  }))}
  fullWidth margin="dense"
/>
            </Grid>
          </Grid>
        </Box>

        {/* Login Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Login</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="SSO"
                value={project.config?.login?.SSO ?? globalConfig.login?.SSO ?? (isNew ? defaultProjectConfig.ui_config.login.SSO : '')}
                onChange={e => setProject(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    login: {
                      ...((prev.config && prev.config.login) || {}),
                      SSO: e.target.value,
                    },
                  },
                }))}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Photo"
                value={project.config?.login?.photo ?? globalConfig.login?.photo ?? (isNew ? defaultProjectConfig.ui_config.login.photo : '')}
                onChange={e => setProject(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    login: {
                      ...((prev.config && prev.config.login) || {}),
                      photo: e.target.value,
                    },
                  },
                }))}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Login Photo"
                value={project.config?.login?.loginPhoto ?? globalConfig.login?.loginPhoto ?? (isNew ? defaultProjectConfig.ui_config.login.loginPhoto : '')}
                onChange={e => setProject(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    login: {
                      ...((prev.config && prev.config.login) || {}),
                      loginPhoto: e.target.value,
                    },
                  },
                }))}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Background Photo (Homepage)"
                value={project.config?.login?.backgroundPhoto ?? globalConfig.login?.backgroundPhoto ?? (isNew ? defaultProjectConfig.ui_config.login.backgroundPhoto : '')}
                onChange={e => setProject(prev => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    login: {
                      ...((prev.config && prev.config.login) || {}),
                      backgroundPhoto: e.target.value,
                    },
                  },
                }))}
                fullWidth
                margin="dense"
                helperText="Leave empty to use Login Photo as background"
              />
            </Grid>
          </Grid>
        </Box>

        {/* CWStatus Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>CWStatus</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField label="Red"
  value={globalConfig.CWStatus?.red ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    CWStatus: {
      ...((prev.CWStatus) || {}),
      red: e.target.value
    }
  }))}
  fullWidth margin="dense"
/>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Grey"
  value={globalConfig.CWStatus?.grey ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    CWStatus: {
      ...((prev.CWStatus) || {}),
      grey: e.target.value
    }
  }))}
  fullWidth margin="dense"
/>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Green"
  value={globalConfig.CWStatus?.green ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    CWStatus: {
      ...((prev.CWStatus) || {}),
      green: e.target.value
    }
  }))}
  fullWidth margin="dense"
/>
            </Grid>
          </Grid>

        </Box>
        {/* Typography Section */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Typography</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Font Family"
  value={globalConfig.typography?.fontFamily ?? ''}
  onChange={e => setGlobalConfig((prev: any) => ({
    ...prev,
    typography: {
      ...((prev.typography) || {}),
      fontFamily: e.target.value
    }
  }))}
  fullWidth margin="dense"
/>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Grid>
</Grid>
                    </CardContent>
                </Card>
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button 
                    variant="outlined" 
                    onClick={handleCancel}
                    sx={{ mr: 2 }}
                >
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSave}
                >
                    {isNew ? 'Create Project' : 'Update Project'}
                </Button>
            </Box>
        </Box>
    );
};

export default ProjectDetail;
